import { MapPinIcon } from '@heroicons/react/24/solid';
import { AdvancedMarker } from '@vis.gl/react-google-maps';
import type React from 'react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import GoogleMap from '@/components/UI/GoogleMap/index.tsx';
import type { MapMarker } from './hooks/useMapMarkers';

interface MapSectionProps {
  markers: MapMarker[];
  className?: string;
}

const MapSection: React.FC<MapSectionProps> = ({ markers, className }) => {
  const mapId = useId();
  const [selectedMarkerId, setSelectedMarkerId] = useState<string | null>(null);
  const [popoverPos, setPopoverPos] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  // Safety check: ensure markers is an array and has valid data
  const safeMarkers = Array.isArray(markers) ? markers : [];
  const validMarkers = safeMarkers.filter(
    (marker) =>
      marker && typeof marker.lat === 'number' && typeof marker.lng === 'number' && !Number.isNaN(marker.lat) && !Number.isNaN(marker.lng)
  );

  const defaultCenter = useMemo(() => {
    // Default to Milwaukee coordinates if no valid markers
    if (validMarkers.length === 0) {
      return { lat: 43.0389, lng: -87.9065 }; // Milwaukee, WI
    }
    return { lat: validMarkers[0].lat, lng: validMarkers[0].lng };
  }, [validMarkers]);

  // Find the selected marker object
  const selectedMarker = selectedMarkerId ? validMarkers.find((m) => m.id === selectedMarkerId) : null;

  const handleMarkerClick = useCallback((e: React.MouseEvent, markerId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedMarkerId(markerId);
    // Position the popover at the click coordinates
    const popoverWidth = 220;
    const popoverHeight = 160;
    const offsetX = popoverWidth / 2;
    const offsetY = popoverHeight + 20; // Add some space between marker and tooltip

    // Calculate initial position
    let left = e.clientX - offsetX;
    let top = e.clientY - offsetY;

    // Ensure tooltip stays within viewport bounds
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Adjust horizontal position if tooltip would go outside viewport
    if (left < 10) left = 10;
    if (left + popoverWidth > viewportWidth - 10) left = viewportWidth - popoverWidth - 10;

    // Adjust vertical position if tooltip would go above viewport
    if (top < 10) {
      // If tooltip would go above viewport, position it below the marker instead
      top = e.clientY + 30;
    }

    // Ensure tooltip doesn't go below viewport
    if (top + popoverHeight > viewportHeight - 10) {
      top = viewportHeight - popoverHeight - 10;
    }

    setPopoverPos({
      left,
      top,
    });
  }, []);

  const handleCloseClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedMarkerId(null);
  }, []);

  // When a marker is selected, calculate its pixel position for the portal popover
  useEffect(() => {
    if (!selectedMarker) {
      setPopoverPos(null);
      return;
    }
  }, [selectedMarker]);

  // Don't render if no valid markers
  if (validMarkers.length === 0) {
    return (
      <div className={`w-full h-[800px] bg-white rounded-[var(--dashboard-radius)] flex items-center justify-center ${className ?? ''}`}>
        <div className='text-neutralColor-medium text-center'>
          <p className='text-lg font-medium mb-2'>No Location Data Available</p>
          <p className='text-sm'>No valid coordinates found in the data.</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={mapContainerRef} className={`w-full h-[800px] bg-white rounded-[var(--dashboard-radius)] ${className ?? ''}`}>
      <GoogleMap defaultCenter={defaultCenter} defaultZoom={11} className='w-full h-full' mapId={mapId}>
        {validMarkers.map((marker) => (
          <AdvancedMarker key={marker.id} position={{ lat: marker.lat, lng: marker.lng }}>
            <MapPinIcon className='h-8 w-8 text-primaryColor-normal cursor-pointer' onClick={(e) => handleMarkerClick(e, marker.id)} />
          </AdvancedMarker>
        ))}
      </GoogleMap>
      {selectedMarker &&
        popoverPos &&
        ReactDOM.createPortal(
          <div
            className='z-50 bg-white border border-neutralColor-light rounded-lg shadow-lg p-4 min-w-[220px] max-w-[260px]'
            style={{
              position: 'fixed',
              left: popoverPos.left,
              top: popoverPos.top,
            }}
          >
            <button
              className='absolute top-1 right-1 text-neutralColor-normal hover:text-tertiaryColor-normal'
              onClick={handleCloseClick}
              aria-label='Close'
            >
              ×
            </button>
            <div className='mb-2'>
              <div className='font-semibold text-primaryColor-normal text-base'>{selectedMarker.address}</div>
            </div>
            <div className='text-xs text-neutralColor-medium mb-1'>
              <span className='font-semibold'>Payee Type:</span> {selectedMarker.payeeType || 'N/A'}
            </div>
            <div className='text-xs text-neutralColor-medium mb-1'>
              <span className='font-semibold'>Amount:</span>{' '}
              {typeof selectedMarker.amountOfPayment === 'number'
                ? selectedMarker.amountOfPayment.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    maximumFractionDigits: 0,
                  })
                : 'N/A'}
            </div>
            <div className='text-xs text-neutralColor-medium mb-1'>
              <span className='font-semibold'>Assistance:</span> {selectedMarker.typeOfAssistance || 'N/A'}
            </div>
            <div className='text-xs text-neutralColor-medium'>
              <span className='font-semibold'>Date:</span>{' '}
              {selectedMarker.dateOfPayment ? new Date(selectedMarker.dateOfPayment).toLocaleDateString() : 'N/A'}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default MapSection;
